import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FileText,
  Scale,
  CheckCircle,
  Clock,
  AlertTriangle,
  LogOut,
  Search,
  Filter,
  Eye,
  Download,
  MessageSquare,
  User,
  Calendar,
  Building2,
  ChevronDown,
  Loader2,
  XCircle,
  RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import AnimatedLogo from "../components/AnimatedLogo";
import NotificationBell from "../components/NotificationBell";

const statusConfig = {
  pending: { label: "En attente", color: "bg-yellow-500", icon: Clock },
  in_progress: { label: "En cours", color: "bg-blue-500", icon: RefreshCw },
  completed: { label: "Terminé", color: "bg-green-500", icon: CheckCircle },
  cancelled: { label: "Annulé", color: "bg-red-500", icon: XCircle }
};

const serviceTypes = {
  regulation: { label: "Rédaction règlement", icon: FileText },
  bailiff: { label: "Contrôle huissier", icon: Scale }
};

export default function JuristDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({ pending: 0, in_progress: 0, completed: 0, total: 0 });
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  
  // Modal states
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [juristNotes, setJuristNotes] = useState("");

  useEffect(() => {
    if (user?.role !== "jurist") {
      navigate("/login");
      return;
    }
    loadOrders();
  }, [user, navigate]);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const res = await api.get("/legal/orders");
      const legalOrders = res.data.filter(o => 
        o.service_type === "regulation" || o.service_type === "bailiff"
      );
      setOrders(legalOrders);
      
      // Calculate stats
      const pending = legalOrders.filter(o => o.status === "pending").length;
      const in_progress = legalOrders.filter(o => o.status === "in_progress").length;
      const completed = legalOrders.filter(o => o.status === "completed").length;
      setStats({ pending, in_progress, completed, total: legalOrders.length });
    } catch (error) {
      toast.error("Erreur lors du chargement des commandes");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const openOrderDetail = (order) => {
    setSelectedOrder(order);
    setJuristNotes(order.jurist_notes || "");
    setShowDetailModal(true);
  };

  const updateOrderStatus = async (newStatus) => {
    if (!selectedOrder) return;
    setUpdatingStatus(true);
    try {
      await api.put(`/legal/orders/${selectedOrder.id}`, { status: newStatus });
      
      // Update jurist notes if changed
      if (juristNotes !== selectedOrder.jurist_notes) {
        await api.put(`/legal/orders/${selectedOrder.id}/jurist-notes`, { notes: juristNotes });
      }
      
      toast.success("Commande mise à jour");
      setShowDetailModal(false);
      loadOrders();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.draw_title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         order.client_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || order.status === statusFilter;
    const matchesType = typeFilter === "all" || order.service_type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-white animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-900 via-purple-800 to-indigo-900 text-white">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <AnimatedLogo variant="header" />
              <div>
                <h1 className="text-xl font-bold">Espace Juriste</h1>
                <p className="text-sm opacity-80">Services juridiques TIREO</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium">{user?.name || "Juriste"}</p>
                <p className="text-xs opacity-80">{user?.email}</p>
              </div>
              <NotificationBell />
              <Button variant="ghost" size="icon" onClick={handleLogout} className="text-white hover:bg-white/20">
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-yellow-50 dark:bg-yellow-950/20 border-yellow-200 dark:border-yellow-800">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-yellow-500 flex items-center justify-center">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-yellow-700 dark:text-yellow-400">{stats.pending}</p>
                <p className="text-sm text-yellow-600 dark:text-yellow-500">En attente</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center">
                <RefreshCw className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-700 dark:text-blue-400">{stats.in_progress}</p>
                <p className="text-sm text-blue-600 dark:text-blue-500">En cours</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-700 dark:text-green-400">{stats.completed}</p>
                <p className="text-sm text-green-600 dark:text-green-500">Terminées</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-purple-50 dark:bg-purple-950/20 border-purple-200 dark:border-purple-800">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-purple-500 flex items-center justify-center">
                <Scale className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-700 dark:text-purple-400">{stats.total}</p>
                <p className="text-sm text-purple-600 dark:text-purple-500">Total</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher par tirage ou client..."
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="in_progress">En cours</SelectItem>
                  <SelectItem value="completed">Terminé</SelectItem>
                  <SelectItem value="cancelled">Annulé</SelectItem>
                </SelectContent>
              </Select>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  <SelectItem value="regulation">Règlement</SelectItem>
                  <SelectItem value="bailiff">Huissier</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={loadOrders}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualiser
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Orders List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Commandes juridiques
            </CardTitle>
            <CardDescription>
              {filteredOrders.length} commande(s) trouvée(s)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {filteredOrders.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Scale className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p>Aucune commande trouvée</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredOrders.map((order) => {
                  const StatusIcon = statusConfig[order.status]?.icon || Clock;
                  const TypeIcon = serviceTypes[order.service_type]?.icon || FileText;
                  return (
                    <motion.div
                      key={order.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center gap-4 p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                      onClick={() => openOrderDetail(order)}
                    >
                      {/* Type Icon */}
                      <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                        <TypeIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                      </div>
                      
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-semibold truncate">{order.draw_title || "Tirage"}</p>
                          <Badge className={`${statusConfig[order.status]?.color || "bg-gray-500"} text-white text-xs`}>
                            {statusConfig[order.status]?.label || order.status}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {order.client_name || "Client"}
                          </span>
                          <span className="flex items-center gap-1">
                            <Building2 className="w-3 h-3" />
                            {order.client_company || "-"}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(order.created_at)}
                          </span>
                        </div>
                      </div>
                      
                      {/* Type Badge */}
                      <Badge variant="outline" className="hidden sm:flex">
                        {serviceTypes[order.service_type]?.label || order.service_type}
                      </Badge>
                      
                      {/* Action */}
                      <Button variant="ghost" size="icon">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Order Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedOrder?.service_type === "regulation" ? (
                <FileText className="w-5 h-5 text-purple-500" />
              ) : (
                <Scale className="w-5 h-5 text-purple-500" />
              )}
              {serviceTypes[selectedOrder?.service_type]?.label || "Commande"}
            </DialogTitle>
            <DialogDescription>
              Commande #{selectedOrder?.id?.substring(0, 8)}
            </DialogDescription>
          </DialogHeader>

          {selectedOrder && (
            <div className="space-y-6 mt-4">
              {/* Status */}
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${statusConfig[selectedOrder.status]?.color}`} />
                  <span className="font-medium">{statusConfig[selectedOrder.status]?.label}</span>
                </div>
                <Select
                  value={selectedOrder.status}
                  onValueChange={(value) => setSelectedOrder(prev => ({ ...prev, status: value }))}
                >
                  <SelectTrigger className="w-[150px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">En attente</SelectItem>
                    <SelectItem value="in_progress">En cours</SelectItem>
                    <SelectItem value="completed">Terminé</SelectItem>
                    <SelectItem value="cancelled">Annulé</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Client Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground text-xs">Client</Label>
                  <p className="font-medium">{selectedOrder.client_name || "-"}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Entreprise</Label>
                  <p className="font-medium">{selectedOrder.client_company || "-"}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Email</Label>
                  <p className="font-medium">{selectedOrder.client_email || "-"}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Téléphone</Label>
                  <p className="font-medium">{selectedOrder.client_phone || "-"}</p>
                </div>
              </div>

              {/* Draw Info */}
              <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
                <Label className="text-muted-foreground text-xs">Tirage concerné</Label>
                <p className="font-semibold text-lg">{selectedOrder.draw_title || "N/A"}</p>
                {selectedOrder.draw_date && (
                  <p className="text-sm text-muted-foreground">
                    Date du tirage : {formatDate(selectedOrder.draw_date)}
                  </p>
                )}
              </div>

              {/* Client Notes */}
              {selectedOrder.client_notes && (
                <div>
                  <Label className="text-muted-foreground text-xs">Notes du client</Label>
                  <p className="p-3 bg-muted rounded-lg text-sm">{selectedOrder.client_notes}</p>
                </div>
              )}

              {/* Jurist Notes */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Notes du juriste
                </Label>
                <Textarea
                  value={juristNotes}
                  onChange={(e) => setJuristNotes(e.target.value)}
                  placeholder="Ajoutez vos notes ici..."
                  rows={4}
                />
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-muted-foreground text-xs">Créée le</Label>
                  <p>{formatDate(selectedOrder.created_at)}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Mise à jour</Label>
                  <p>{formatDate(selectedOrder.updated_at)}</p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>
              Fermer
            </Button>
            <Button
              onClick={() => updateOrderStatus(selectedOrder?.status)}
              disabled={updatingStatus}
            >
              {updatingStatus ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
